import {_Window} from 'openfin/_v2/api/window/window';
import {WindowOption} from 'openfin/_v2/api/window/windowOption';
import {Signal} from 'openfin-service-signal';
import {Transition, TransitionOptions, Bounds, AnchorType} from 'openfin/_v2/shapes';
import {injectable} from 'inversify';
import {Identity} from 'openfin/_v2/main';
import {Point} from 'openfin/_v2/api/system/point';

import {Manifest} from '../types/manifest';
import {Dimensions} from '../types/interfaces';

import {WebWindowFactory, WebWindow} from './WebWindow';

@injectable()
export class FinWebWindowFactory implements WebWindowFactory {
    public async create(options: WindowOption): Promise<WebWindow> {
        const window = await fin.Window.create(options).catch(() => {
            console.info('Failed to create existing window, wrapping instead', options.name);
            return fin.Window.wrapSync({name: options.name, uuid: fin.Window.me.uuid});
        });
        return new FinWebWindow(window);
    }

    public wrap(identity: Identity): WebWindow {
        const window = fin.Window.wrapSync(identity);
        return new FinWebWindow(window);
    }
}

export class FinWebWindow implements WebWindow {
    public readonly onMouseEnter: Signal<[]> = new Signal();
    public readonly onMouseLeave: Signal<[]> = new Signal();
    public readonly onBlurred: Signal<[]> = new Signal();
    public readonly onRestore: Signal<[]> = new Signal();
    public readonly onMinimize: Signal<[]> = new Signal();

    private readonly _document: Document;
    private readonly _window: _Window;
    private readonly _nativeWindow: Window;

    private _isActive: boolean;
    private _closePromise!: Promise<void>;

    constructor(window: _Window, nativeWindow?: Window) {
        this._window = window;
        this._nativeWindow = nativeWindow || window.getWebWindow();
        try {
            this._document = this._nativeWindow.document;
        } catch (error) {
            throw new Error(`Unable to get the document, this is most likely due to CORS: ${error}`);
        }
        this._isActive = true;

        this.setupListeners();
    }

    public get document(): Document {
        return this._document;
    }

    public get nativeWindow(): Window {
        return this._nativeWindow;
    }

    public async restore(): Promise<void> {
        return this._window.restore();
    }

    public async minimize(): Promise<void> {
        return this._window.minimize();
    }

    public async isShowing(): Promise<boolean> {
        return this._window.isShowing();
    }

    public async show(): Promise<void> {
        if (this._isActive) {
            await this._window.updateOptions({opacity: 1});
            return this._window.show();
        }
    }

    public async showAt(left: number, top: number): Promise<void> {
        if (this._isActive) {
            await this._window.updateOptions({opacity: 1});
            return this._window.showAt(left, top);
        }
    }

    public async hide(): Promise<void> {
        if (this._isActive) {
            await this._window.updateOptions({opacity: 0});
            return this._window.hide();
        }
    }

    public async setAsForeground(): Promise<void> {
        await this._window.setAsForeground();
        // Focus occurs with `setAsForeground` on Windows but not macOS, so do this for consistency and to make sure we can get a blur event later
        await this._window.focus();
    }

    public async animate(transition: Transition, options: TransitionOptions): Promise<void> {
        if (this._isActive) {
            return this._window.animate(transition, options);
        }
    }

    public async setBounds(bounds: Bounds): Promise<void> {
        if (this._isActive) {
            return this._window.setBounds(bounds);
        }
    }

    public async close(force: boolean = false): Promise<void> {
        if (this._isActive) {
            this._isActive = false;
            this._closePromise = this._window.close(force);
        }

        return this._closePromise;
    }

    public async moveTo(position: Point): Promise<void> {
        if (this._isActive) {
            return this._window.moveTo(position.x, position.y);
        }
    }

    public async resizeTo(dimensions: Dimensions, anchor: AnchorType = 'top-left'): Promise<void> {
        if (this._isActive) {
            return this._window.resizeTo(dimensions.width, dimensions.height, anchor);
        }
    }

    public async getManifest(): Promise<Manifest> {
        return (await this._window.getParentApplication()).getManifest();
    }

    private setupListeners(): void {
        this._window.addListener('closing', () => {
            this._isActive = false;
        });
        document.addEventListener('mouseenter', () => this.onMouseEnter.emit());
        document.addEventListener('mouseleave', () => this.onMouseLeave.emit());
        this._window.addListener('blurred', () => this.onBlurred.emit());
        this._window.addListener('restored', () => this.onRestore.emit());
        this._window.addListener('minimized', () => this.onMinimize.emit());
    }
}
